import { useState, useCallback, useRef, useEffect } from 'react';
import api from '../lib/api';
import type { Contribution, CFQuery, CFSummary, CFMonthlyData, CFTopEntry } from '../types/campaignFinance';
import { buildQueryParams, CF_REQUEST_TIMEOUT_MS } from '../utils/campaignFinanceUtils';

const PAGE_SIZE = 100;

export interface DateHint {
  totalWithoutDateFilter: number;
  minDate: string | null;
  maxDate: string | null;
}

interface UseCFContributionsReturn {
  contributions: Contribution[];
  total: number;
  hasMore: boolean;
  loading: boolean;
  loadingMore: boolean;
  showingAll: boolean;
  error: string | null;
  summary: CFSummary | null;
  beginningBalance: number | null;
  dateHint: DateHint | null;
  monthlyTrend: CFMonthlyData[];
  topContributors: CFTopEntry[];
  topRecipients: CFTopEntry[];
  sortBy: string;
  sortDir: 'asc' | 'desc';
  setSortBy: (field: string) => void;
  setSort: (field: string, direction: 'asc' | 'desc') => void;
  toggleSort: (field: string) => void;
  search: (query: CFQuery) => Promise<void>;
  loadMore: () => Promise<void>;
  showAll: () => Promise<void>;
  showPaginated: () => void;
  reset: () => void;
}

export function useCFContributions(): UseCFContributionsReturn {
  const [contributions, setContributions] = useState<Contribution[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<CFSummary | null>(null);
  const [beginningBalance, setBeginningBalance] = useState<number | null>(null);
  const [dateHint, setDateHint] = useState<DateHint | null>(null);
  const [monthlyTrend, setMonthlyTrend] = useState<CFMonthlyData[]>([]);
  const [topContributors, setTopContributors] = useState<CFTopEntry[]>([]);
  const [topRecipients, setTopRecipients] = useState<CFTopEntry[]>([]);
  const [sortBy, setSortByState] = useState('contribution_date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const [showingAll, setShowingAll] = useState(false);

  const offsetRef = useRef(0);
  const queryRef = useRef<CFQuery>({});
  const hasSearchedRef = useRef(false);

  const search = useCallback(async (query: CFQuery) => {
    try {
      setLoading(true);
      setError(null);
      queryRef.current = query;
      hasSearchedRef.current = true;
      offsetRef.current = 0;
      setShowingAll(false);
      // Clear prior results immediately so stale data doesn't linger during the fetch.
      setContributions([]);
      setTotal(0);
      setSummary(null);
      setMonthlyTrend([]);
      setTopContributors([]);
      setTopRecipients([]);
      setBeginningBalance(null);
      setDateHint(null);

      const params = buildQueryParams(query, {
        offset: 0,
        limit: PAGE_SIZE,
        sort_by: sortBy,
        sort_order: sortDir,
      });

      const response = await api.get(`/campaign-finance?${params}`, { timeout: CF_REQUEST_TIMEOUT_MS });
      if (response.data.success) {
        const data = response.data.data;
        setContributions(data.contributions || []);
        setTotal(data.total || 0);
        setHasMore(data.hasMore || false);
        offsetRef.current = PAGE_SIZE;

        if (data.summary) {
          setSummary({
            total: Number(data.summary.total_amount) || 0,
            count: Number(data.summary.total_contributions) || 0,
            contributors: Number(data.summary.unique_contributors) || 0,
            recipients: Number(data.summary.unique_committees) || 0,
            avg: Number(data.summary.average_amount) || 0,
            median: Number(data.summary.median_amount) || 0,
            largest: Number(data.summary.max_amount) || 0,
          });
        }
        setBeginningBalance(data.beginningBalance ?? null);
        setMonthlyTrend(data.monthlyTrend || []);
        setTopContributors(data.topContributors || []);
        setTopRecipients(data.topRecipients || []);
        setDateHint(data.dateHint || null);
      }
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message;
      setError(msg || 'Failed to fetch contributions');
      setContributions([]);
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortDir]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    try {
      setLoadingMore(true);
      const params = buildQueryParams(queryRef.current, {
        offset: offsetRef.current,
        limit: PAGE_SIZE,
        sort_by: sortBy,
        sort_order: sortDir,
      });

      const response = await api.get(`/campaign-finance?${params}`, { timeout: CF_REQUEST_TIMEOUT_MS });
      if (response.data.success) {
        const data = response.data.data;
        setContributions(prev => [...prev, ...(data.contributions || [])]);
        setHasMore(data.hasMore || false);
        offsetRef.current += PAGE_SIZE;
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, sortBy, sortDir]);

  const toggleSort = useCallback((field: string) => {
    if (field === sortBy) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortByState(field);
      setSortDir('desc');
    }
  }, [sortBy]);

  // Re-fetch when sort changes (only after an initial search)
  useEffect(() => {
    if (!hasSearchedRef.current) return;
    const q = queryRef.current;
    const doRefetch = async () => {
      try {
        setLoading(true);
        setError(null);
        offsetRef.current = 0;
        const params = buildQueryParams(q, { offset: 0, limit: PAGE_SIZE, sort_by: sortBy, sort_order: sortDir });
        const response = await api.get(`/campaign-finance?${params}`, { timeout: CF_REQUEST_TIMEOUT_MS });
        if (response.data.success) {
          const data = response.data.data;
          setContributions(data.contributions || []);
          setTotal(data.total || 0);
          setHasMore(data.hasMore || false);
          offsetRef.current = PAGE_SIZE;
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch contributions');
      } finally {
        setLoading(false);
      }
    };
    doRefetch();
  }, [sortBy, sortDir]);

  const setSortBy = useCallback((field: string) => {
    setSortByState(field);
  }, []);

  const setSort = useCallback((field: string, direction: 'asc' | 'desc') => {
    setSortByState(field);
    setSortDir(direction);
  }, []);

  const showAll = useCallback(async () => {
    if (!hasSearchedRef.current || total <= 0) return;
    try {
      setLoading(true);
      setError(null);
      const params = buildQueryParams(queryRef.current, {
        offset: 0,
        limit: total,
        sort_by: sortBy,
        sort_order: sortDir,
      });
      const response = await api.get(`/campaign-finance?${params}`, { timeout: CF_REQUEST_TIMEOUT_MS });
      if (response.data.success) {
        const data = response.data.data;
        setContributions(data.contributions || []);
        setTotal(data.total || 0);
        setHasMore(false);
        setShowingAll(true);
        offsetRef.current = data.total || 0;
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch all contributions');
    } finally {
      setLoading(false);
    }
  }, [total, sortBy, sortDir]);

  const showPaginated = useCallback(() => {
    if (!hasSearchedRef.current) return;
    setShowingAll(false);
    const q = queryRef.current;
    const doRefetch = async () => {
      try {
        setLoading(true);
        setError(null);
        offsetRef.current = 0;
        const params = buildQueryParams(q, { offset: 0, limit: PAGE_SIZE, sort_by: sortBy, sort_order: sortDir });
        const response = await api.get(`/campaign-finance?${params}`, { timeout: CF_REQUEST_TIMEOUT_MS });
        if (response.data.success) {
          const data = response.data.data;
          setContributions(data.contributions || []);
          setTotal(data.total || 0);
          setHasMore(data.hasMore || false);
          offsetRef.current = PAGE_SIZE;
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch contributions');
      } finally {
        setLoading(false);
      }
    };
    doRefetch();
  }, [sortBy, sortDir]);

  const reset = useCallback(() => {
    setContributions([]);
    setTotal(0);
    setHasMore(false);
    setShowingAll(false);
    setError(null);
    setSummary(null);
    setBeginningBalance(null);
    setDateHint(null);
    setMonthlyTrend([]);
    setTopContributors([]);
    setTopRecipients([]);
    offsetRef.current = 0;
    queryRef.current = {};
  }, []);

  return {
    beginningBalance,
    contributions,
    total,
    hasMore,
    loading,
    loadingMore,
    showingAll,
    error,
    summary,
    dateHint,
    monthlyTrend,
    topContributors,
    topRecipients,
    sortBy,
    sortDir,
    setSortBy,
    setSort,
    toggleSort,
    search,
    loadMore,
    showAll,
    showPaginated,
    reset,
  };
}
